"use client";

import { FormEvent, useEffect, useState } from "react";
import Link from "next/link";
import { track } from "@/components/Analytics";
import { legacyUtmFields, readAttribution } from "@/lib/attribution";
import {
  FloatInput,
  FloatSelect,
  FloatTextarea,
  validateLeadFields,
  validators,
} from "@/components/FloatField";

type Status = "idle" | "submitting" | "success" | "error";

declare global {
  interface Window {
    grecaptcha?: { getResponse: () => string; reset: () => void };
  }
}

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

// The site's single lead-capture form (one unified style everywhere). Posts to
// /api/leads and auto-captures the source page + UTM parameters. When
// `hiddenLocation` is set (centre/building enquiries), the location travels
// with the lead invisibly to the end user and surfaces in /admin/leads for
// review. reCAPTCHA renders when NEXT_PUBLIC_RECAPTCHA_SITE_KEY is configured.
// Last-resort city list, used only if a caller renders the form without
// passing the published locations (see lib/cityOptions).
const FALLBACK_CITIES = [
  "Bengaluru", "Hyderabad", "Noida", "Gurugram",
  "New Delhi", "Mumbai", "Chennai", "Pune",
];

export function LeadForm({
  sourceLabel,
  hiddenLocation,
  hideJobSeeker = false,
  extraField,
  cityOptions = FALLBACK_CITIES,
  compact = false,
}: {
  sourceLabel?: string;
  hiddenLocation?: string;
  hideJobSeeker?: boolean;
  /** Single-column, tighter form for a narrow rail — the blog article
   *  template's 285px sidebar. The two-up field grid is keyed off `sm:`,
   *  which is the VIEWPORT not the container, so on a desktop blog page it
   *  would still split into two columns inside a 285px card and crush every
   *  field. This forces one column regardless of viewport. */
  compact?: boolean;
  /** Optional extra free-text field (placeholder). Its value folds into the
   * message payload. Used e.g. on Enterprise Services. */
  extraField?: string;
  /** City dropdown choices — the published locations, resolved server-side by
   * lib/cityOptions and passed down. Defaults to the static list below only
   * when a caller does not supply them. */
  cityOptions?: string[];
}) {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Anti-bot time-trap: humans take a few seconds between seeing the form and
  // submitting; bots post instantly. Elapsed ms travels with the payload and
  // is enforced server-side (same clock, so no client/server skew issues).
  const [mountedAt] = useState(() => Date.now());

  // Load the reCAPTCHA script once, only when a site key is configured.
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const s = document.createElement("script");
    s.src = "https://www.google.com/recaptcha/api.js";
    s.async = true;
    document.head.appendChild(s);
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    // Basic client-side validation (email format, 10-digit mobile, required
    // text) — the API validates again server-side.
    const errs = validateLeadFields(form, {
      name: validators.required("your name"),
      email: validators.email,
      company: validators.required("your company name"),
      city: (v) => (v ? null : "Please select a city"),
      phone: validators.mobile,
      message: validators.required("your requirement"),
    });
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    const data = Object.fromEntries(new FormData(form).entries());

    // Honeypot tripped — silently pretend success (real users never fill it).
    if (typeof data.website === "string" && data.website.trim() !== "") {
      setStatus("success");
      return;
    }
    (data as Record<string, unknown>).form_elapsed_ms = Date.now() - mountedAt;

    // City + job-seeker travel inside the message payload (the leads table
    // has no dedicated columns); they surface in /admin/leads with the lead.
    {
      const { city, job_seeker, message, inquiry, ...rest } =
        data as Record<string, string>;
      const composed = [
        message,
        inquiry ? `Inquiry: ${inquiry}` : null,
        city ? `City: ${city}` : null,
        job_seeker ? "I am a Job Seeker" : null,
      ]
        .filter(Boolean)
        .join("\n");
      Object.keys(data).forEach((k) => delete (data as Record<string, unknown>)[k]);
      Object.assign(data, rest, { message: composed });
    }

    if (RECAPTCHA_SITE_KEY) {
      const token = window.grecaptcha?.getResponse() ?? "";
      if (!token) {
        setStatus("error");
        setError("Please complete the reCAPTCHA check.");
        return;
      }
      (data as Record<string, unknown>).recaptcha_token = token;
    }

    // Where they came from, recorded when they ARRIVED rather than read off
    // the current URL — see lib/attribution for why that distinction is the
    // whole difference between having campaign data and not.
    const attribution = readAttribution();

    const payload = {
      ...data,
      source_page:
        (hiddenLocation ? `${hiddenLocation} — ` : "") +
        (sourceLabel ??
          (typeof window !== "undefined" ? window.location.pathname : "")),
      ...legacyUtmFields(attribution),
      attribution,
    };

    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Request failed");
      track("lead_submitted", { source: "contact-form" });
      setStatus("success");
      form.reset();
      window.grecaptcha?.reset();
    } catch {
      track("lead_failed", { source: "contact-form" });
      setStatus("error");
      setError("Something went wrong. Please try again or email us directly.");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-xl border border-brand-line bg-brand-surface p-6">
        <p className="font-medium">Thank you — we’ve received your enquiry.</p>
        <p className="mt-1 text-sm text-brand-slate">
          A member of our team will be in touch with you shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`relative grid ${compact ? "gap-3" : "gap-4"}`}>
      {/* location context — hidden from the end user, reviewed in /admin/leads */}
      {hiddenLocation && (
        <input type="hidden" name="location" value={hiddenLocation} />
      )}
      {/* Honeypot — invisible to humans (off-screen, skipped by tab order and
          screen readers); form-filling bots complete it and get rejected. */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
        <label>
          Website
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            defaultValue=""
          />
        </label>
      </div>
      <div className={compact ? "grid gap-3" : "grid gap-4 sm:grid-cols-2"}>
        <FloatInput name="name" label="Name *" required rule={validators.required("your name")} error={fieldErrors.name} />
        <FloatInput name="email" type="email" label="Email *" required rule={validators.email} error={fieldErrors.email} />
        <FloatInput name="company" label="Company Name *" required rule={validators.required("your company name")} error={fieldErrors.company} />
        <FloatSelect name="city" label="City *" required defaultValue="" error={fieldErrors.city}>
          <option value="" disabled />
          {cityOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </FloatSelect>
        <FloatInput
          name="phone"
          type="tel"
          inputMode="numeric"
          label="Mobile Number *"
          required
          rule={validators.mobile}
          error={fieldErrors.phone}
          className={compact ? "" : "sm:col-span-2"}
        />
      </div>
      <FloatTextarea
        name="message"
        rows={compact ? 3 : 4}
        label="Share Your Requirement *"
        required
        rule={validators.required("your requirement")}
        error={fieldErrors.message}
      />
      {extraField && <FloatTextarea name="inquiry" rows={3} label={extraField} />}

      {!hideJobSeeker && (
        <label className="flex items-start gap-2.5 text-sm text-brand-slate">
          <input type="checkbox" name="job_seeker" className="mt-1 accent-[#447973]" />
          <span>I am a Job Seeker</span>
        </label>
      )}

      <label className={`flex items-start gap-2.5 text-brand-slate ${compact ? "text-xs" : "text-sm"}`}>
        <input type="checkbox" required className="mt-1 accent-[#447973]" />
        <span>
          I agree to the{" "}
          <Link href="/terms-and-conditions" className="text-brand-accent-dark underline decoration-brand-line underline-offset-2 hover:text-brand-ink">
            Terms &amp; Conditions
          </Link>{" "}
          and{" "}
          <Link href="/privacy-policy" className="text-brand-accent-dark underline decoration-brand-line underline-offset-2 hover:text-brand-ink">
            Privacy Policy
          </Link>
          .
        </span>
      </label>

      {RECAPTCHA_SITE_KEY && (
        <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} />
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={status === "submitting"}
        className={`btn-primary disabled:opacity-60 ${compact ? "w-full justify-center" : "justify-self-start"}`}
      >
        {status === "submitting" ? "Sending…" : "Submit"}
      </button>
    </form>
  );
}
